export default DesktopNotifications;

import { BellIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { navigate } from 'vike/client/router';

import srcRasLogo from '@/components/shared/ras-logo-512-emerald-600.png';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';

// Page-constructed Notifications only (the `new Notification()` fallback used on
// a plain browser tab). Notifications raised via the service worker are tracked
// by the SW itself and closed through registration.getNotifications().
const openedNotifications: Notification[] = [];

const hasFocus = () => typeof document !== 'undefined' && document.hasFocus();

const useWindowFocus = () => {
  const [focused, setFocused] = useState(hasFocus); // Focus for first render

  useEffect(() => {
    setFocused(hasFocus()); // Focus for additional renders

    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return focused;
};

export function DesktopNotifications({ isMain }: { isMain?: boolean }) {
  const [show, setShow] = useState(window.Notification && Notification.permission !== 'granted');
  const focused = useWindowFocus();
  const idle = useIdleContext();

  const isPwa = useMediaQuery('(display-mode: standalone)');
  const [pollTime, setPollTime] = useState<number | null>(isPwa ? 5000 : null);
  const pollCount = useRef(isPwa ? 0 : 121);

  useInterval(() => {
    // On PWA, we may need to poll until they give the whole PWA notification permission on macos
    if (!isPwa || !show || pollCount.current > 120) {
      pollCount.current = 121;
      setPollTime(null);

      return;
    }

    if (Notification.permission === 'granted') {
      pollCount.current = 121;

      // Route the confirmation through openNotification so it uses the service
      // worker on an installed PWA (a page `new Notification()` won't show there).
      openNotification('RAS', 'Thank you for enabling notifications.', undefined, 'ras-enabled');

      setPollTime(null);
      setShow(false);
    }
  }, pollTime);

  const enable = useCallback(() => {
    if (isPwa) {
      pollCount.current = 0;
      setPollTime(2000);
    }

    Notification.requestPermission((permission) => {
      if (permission === 'granted') {
        pollCount.current = 121;

        // See note above: prefer the SW so the confirmation actually appears on
        // an installed PWA.
        openNotification('RAS', 'Thank you for enabling notifications.', undefined, 'ras-enabled');

        setPollTime(null);
        setShow(false);
      }
    });
  }, []);

  useEffect(() => {
    clearCurrentPageNotifications();
  }, []);

  useUpdateEffect(() => {
    if (!idle.inactive) {
      clearCurrentPageNotifications();
    }
  }, [idle.inactive]);

  useEffect(() => {
    const visible = !document.hidden && document.hasFocus();

    if (visible && focused) {
      clearCurrentPageNotifications();
    }
  }, [focused]);

  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isApp = !isDesktop || isPwa;

  // The `isMain` overlay variant is unused (PageLayout renders it, but it has
  // always returned null here); the live control is the sidebar-footer button.
  if (isMain) {
    return null;
  }

  if (!show) {
    return null;
  }

  const label = `Enable ${isApp ? 'app' : 'desktop'} notifications`;

  return (
    // shrink-0 so the flex row (shared with the full-width UserDropdown) can't
    // squeeze the button to zero width. `group` drives the hover/focus tooltip.
    <div className="group relative shrink-0 self-center">
      <button
        type="button"
        onClick={enable}
        aria-label={label}
        title={label}
        className={clsx(
          'flex size-10 items-center justify-center rounded-sm',
          'bg-zinc-800 text-zinc-200 hover:bg-zinc-700 hover:text-white',
          'focus:bg-zinc-700 focus:text-white focus:outline-none',
        )}
      >
        <BellIcon height={20} className="h-5 w-5" aria-hidden="true" />
      </button>
      {/*
        Tooltip: hover/focus reveal on desktop. `pointer-events-none` so it can
        never intercept the button's click (the previous absolute label had no
        such guard and mis-sized the hit area — the "unclickable" bug). Anchored
        to the button's right edge so it stays inside the sidebar. On touch there
        is no hover, but the bell itself is a plain, always-visible tap target,
        so no reveal is needed to reach it.
      */}
      <span
        role="tooltip"
        className={clsx(
          'pointer-events-none absolute right-0 bottom-full z-50 mb-2 whitespace-nowrap rounded-sm px-2 py-1 text-xs leading-4',
          'bg-zinc-900 text-zinc-100 shadow-md ring-1 ring-white/10',
          'opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100',
        )}
      >
        {label}
      </span>
    </div>
  );
}

// ── Notification firing ──────────────────────────────────────────────────────

/**
 * Show a notification via the service worker when one is registered. Installed
 * PWAs (standalone display-mode) can only raise notifications this way — the
 * page `new Notification()` constructor is a no-op there. Returns false when no
 * SW is available so the caller can fall back to a page Notification.
 */
async function showViaServiceWorker(title: string, options: NotificationOptions): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration) {
      await registration.showNotification(title, options);

      return true;
    }
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 * Page-tab fallback: `new Notification()`. Only reached when there's no service
 * worker registration (a plain desktop browser tab). Click navigation and
 * clean-up are wired here; SW notifications handle clicks in the SW's
 * `notificationclick` event instead.
 */
function firePageNotification(title: string, options: NotificationOptions, url?: string) {
  try {
    const notif = new Notification(title, options);

    if (url) {
      openedNotifications.push(notif);

      notif.onclick = function (event) {
        window.focus();
        navigate(url);
        this.close?.();
        clearCurrentPageNotifications();
        event.preventDefault();
      };

      notif.onclose = function () {
        const index = openedNotifications.indexOf(this);

        if (index > -1) {
          openedNotifications.splice(index, 1);
        }
      };
    } else {
      notif.onclick = function (event) {
        window.focus();
        this?.close?.();
        event.preventDefault();
      };
    }
  } catch (error) {
    console.error(error);
  }
}

export function clearPageNotifications(url: string) {
  // Page-tab (fallback) notifications.
  for (let i = openedNotifications.length; i--; ) {
    const notification = openedNotifications[i];

    if (notification.data?.url && url === notification.data.url) {
      try {
        openedNotifications.splice(i, 1);
        notification.close?.();
      } catch (error) {
        console.log(error);
      }
    }
  }

  // Service-worker notifications for the same URL.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.getNotifications())
      .then((notifications) => {
        notifications?.forEach((notification) => {
          const data = notification.data as { url?: string } | undefined;

          if (data?.url && data.url === url) {
            notification.close();
          }
        });
      })
      .catch((error) => console.log(error));
  }
}

export function clearCurrentPageNotifications() {
  if (!IS_SERVER) {
    clearPageNotifications(window.location.pathname);
  }
}

export function openNotification(
  title: string,
  body?: string,
  url?: string,
  tag?: string,
  silent?: boolean,
) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false;
  }

  const tagValue = tag || url;
  const options: NotificationOptions = {
    body,
    data: { url },
    icon: srcRasLogo,
    silent,
    tag: tagValue,
  };

  // Prefer the service worker (required on installed PWAs); fall back to a page
  // Notification only when there's no SW registration (plain desktop tab).
  showViaServiceWorker(title, options).then((shown) => {
    if (!shown) {
      firePageNotification(title, options, url);
    }
  });

  // Permission is granted and we've dispatched a notification attempt; callers
  // use this to flag the message as OPENED.
  return true;
}
